/**
 * AKSESSUAR CHIZMASI — geometriya va hisob
 *
 * Model: profil = NUQTALAR zanjiri (mm). Segmentlar (uzunlik + burchak)
 * shundan hosil qilinadi. Nuqta modeli tanlangani ikki uchdan ham tahrirlash
 * va chizishni oson qiladi.
 *
 * ⚠️ RASXOD QOIDASI (eng muhimi):
 *    Rulon faqat TENG bo'laklarga bo'linadi. Chizma 290 mm bo'lsa ham
 *    1250 dan 4 bo'lakdan ortig'i chiqmaydi — demak har bo'lak 312.5 mm
 *    joy egallaydi va hisob shundan yuritiladi.
 *    Lekin ortiqcha KATTA bo'lsa (masalan 155 mm), u alohida tor rulon
 *    bo'lib omborga qaytadi — u holda mijoz faqat o'z chizmasi uchun to'laydi.
 */

export const HEM_LEN = 15          // mm — qayirma standart uzunligi
export const HEM_ANG = 12          // ° — ochiq qayirma burchagi
export const HEM_R = 4             // mm — 2D da buklama yoyi radiusi
export const MIN_SEG = 15          // mm — eng kichik oddiy segment
export const MIN_HEM = 10          // mm — eng kichik qayirma
export const REMNANT_MIN = 100     // mm — shundan katta qoldiq ishlatiladi

export const snapTo = (v, step) => Math.round(v / step) * step

/* ═══════════ BURCHAK: ichki ↔ bukilish ═══════════
   Ichida ICHKI burchak saqlanadi (to'g'ri chiziq = 180°).
   Ustaga esa BUKILISH ko'rsatiladi — «necha gradusga bukiladi»:
       ichki 90°  → bukilish 90°
       ichki 135° → bukilish 45°
   Tunuka ustalari aynan shu tilda gapiradi. */
export const toBend = ang => (ang == null ? null : Math.round(180 - ang))
export const fromBend = bend => Math.max(1, Math.min(179, 180 - (Number(bend) || 0)))

/* ═══════════ segmentlar ═══════════ */
export function segsOf(pts) {
	const out = []
	for (let i = 0; i < pts.length - 1; i++) {
		const a = pts[i], b = pts[i + 1]
		const len = Math.hypot(b.x - a.x, b.y - a.y)
		let ang = null, flip = false
		if (i > 0) {
			const p = pts[i - 1]
			const a1 = Math.atan2(a.y - p.y, a.x - p.x)
			const a2 = Math.atan2(b.y - a.y, b.x - a.x)
			const d = ((a2 - a1) * 180 / Math.PI + 540) % 360 - 180
			ang = Math.round(180 - Math.abs(d))
			flip = d < 0
		}
		out.push({ len: Math.round(len), ang, flip })
	}
	return out
}

export const blankOf = pts => segsOf(pts).reduce((a, s) => a + s.len, 0)

/* uzunlikni o'zgartirish — keyingi nuqtalar siljiydi */
export function setSegLen(pts, i, v) {
	const next = pts.map(p => ({ ...p }))
	const a = next[i], b = next[i + 1]
	const L = Math.hypot(b.x - a.x, b.y - a.y) || 1
	const nb = { x: a.x + (b.x - a.x) / L * v, y: a.y + (b.y - a.y) / L * v }
	const dx = nb.x - b.x, dy = nb.y - b.y
	for (let k = i + 1; k < next.length; k++) { next[k].x += dx; next[k].y += dy }
	return next
}

/* burchakni o'zgartirish — qolgan zanjir nuqta atrofida aylanadi */
export function setSegAng(pts, i, v) {
	const cur = segsOf(pts)[i]?.ang
	if (cur == null) return pts
	const next = pts.map(p => ({ ...p }))
	const p = next[i - 1], a = next[i], b = next[i + 1]
	const a1 = Math.atan2(a.y - p.y, a.x - p.x)
	const a2 = Math.atan2(b.y - a.y, b.x - a.x)
	const sign = Math.sign(((a2 - a1) * 180 / Math.PI + 540) % 360 - 180) || 1
	const rot = (cur - v) * sign * Math.PI / 180
	const c = Math.cos(rot), s = Math.sin(rot)
	for (let k = i + 1; k < next.length; k++) {
		const dx = next[k].x - a.x, dy = next[k].y - a.y
		next[k] = { x: a.x + dx * c - dy * s, y: a.y + dx * s + dy * c }
	}
	return next
}

/* birinchi segmentning MUTLAQ yo'nalishi — butun zanjir buriladi */
/* chizmaning boshlang'ich yo'nalishi (1-segment qayoqqa qaragan), gradusda.
   ⚠️ Bu qiymat draw_json ga YOZILADI — bo'lmasa chizma qayta ochilganda
      har doim -90 (tepaga) dan qurilib, BURILGAN holatda chiqadi. */
export const dir0Of = pts => pts.length > 1
	? Math.atan2(pts[1].y - pts[0].y, pts[1].x - pts[0].x) * 180 / Math.PI
	: -90

/* butun chizmani o'z markazi atrofida burish (uzunlik/burchak o'zgarmaydi) */
export function rotatePts(pts, deg) {
	if (pts.length < 2) return pts
	const cx = pts.reduce((a, p) => a + p.x, 0) / pts.length
	const cy = pts.reduce((a, p) => a + p.y, 0) / pts.length
	const r = deg * Math.PI / 180, c = Math.cos(r), s = Math.sin(r)
	return pts.map(p => {
		const dx = p.x - cx, dy = p.y - cy
		return { x: cx + dx * c - dy * s, y: cy + dx * s + dy * c }
	})
}

/* oyna aksi — vertikal o'q bo'yicha. Razvyortka o'zgarmaydi, lekin detal
   «chap tomonlama» dan «o'ng tomonlama» ga aylanadi (burilishlar teskarisiga). */
export function mirrorPts(pts) {
	if (pts.length < 2) return pts
	const cx = pts.reduce((a, p) => a + p.x, 0) / pts.length
	return pts.map(p => ({ x: 2 * cx - p.x, y: p.y }))
}

export function setDir0(pts, deg) {
	if (pts.length < 2) return pts
	const next = pts.map(p => ({ ...p }))
	const cur = Math.atan2(next[1].y - next[0].y, next[1].x - next[0].x) * 180 / Math.PI
	const r = (deg - cur) * Math.PI / 180, c = Math.cos(r), s = Math.sin(r)
	const o = next[0]
	for (let k = 1; k < next.length; k++) {
		const dx = next[k].x - o.x, dy = next[k].y - o.y
		next[k] = { x: o.x + dx * c - dy * s, y: o.y + dx * s + dy * c }
	}
	return next
}

/* segment o'chirish — chetdagisi bo'lsa qayirma bayrog'i ham tozalanadi.
   ⚠️ Chegara `pts.length <= 2`, ya'ni OXIRGI segment o'chirilmaydi: bitta
      nuqtali «yarim» chizma hech qayerda ishlamaydi (rasm ham, razvyortka ham
      yo'q). Butunlay tozalash uchun oynadagi «Тозалаш» tugmasi bor.
      Ilgari chegara `<= 3` edi — 2 ta segmentli chizmada ham ✕ o'lik turardi. */
export function delSegAt(pts, hem, i) {
	if (pts.length <= 2 || i < 0 || i >= pts.length - 1) return { pts, hem }
	const next = pts.map(p => ({ ...p }))
	if (i === next.length - 2) next.pop()
	else {
		const a = next[i], b = next[i + 1], dx = a.x - b.x, dy = a.y - b.y
		for (let k = i + 1; k < next.length; k++) { next[k].x += dx; next[k].y += dy }
		next.splice(i + 1, 1)
	}
	const h = { ...hem }
	if (i === 0) h.first = false
	if (i >= next.length - 1) h.last = false
	// qayirma alohida segment — 2 nuqta qolganda unga joy yo'q
	if (next.length < 3) { h.first = false; h.last = false }
	return { pts: next, hem: h }
}

/* ═══════════ qayirma ═══════════
   Tunukani uchidan O'ZIGA qaytarib bosish — ALOHIDA qo'shimcha segment.
   Mavjud segmentga tegmaydi. */
export function hemPointFor(pts, which, dir) {
	const n = pts.length
	const A = HEM_ANG * (dir || 1) * Math.PI / 180
	if (which === 'first') {
		const p0 = pts[0], p1 = pts[1]
		const d = Math.atan2(p1.y - p0.y, p1.x - p0.x) + A
		return { x: p0.x + HEM_LEN * Math.cos(d), y: p0.y + HEM_LEN * Math.sin(d) }
	}
	const a = pts[n - 1], b = pts[n - 2]
	const d = Math.atan2(b.y - a.y, b.x - a.x) - A
	return { x: a.x + HEM_LEN * Math.cos(d), y: a.y + HEM_LEN * Math.sin(d) }
}

/* yo'nalishni almashtirish — FAQAT uch nuqta buriladi, chizma buzilmaydi */
export function flipHemDirIn(pts, hem, which) {
	if (!hem[which] || pts.length < 3) return { pts, hem }
	const h = { ...hem, [which + 'Dir']: -(hem[which + 'Dir'] || 1) }
	const next = pts.map(p => ({ ...p }))
	const n = next.length
	const A = HEM_ANG * h[which + 'Dir'] * Math.PI / 180
	if (which === 'first') {
		const V = next[1], B = next[2]
		const len = Math.hypot(next[0].x - V.x, next[0].y - V.y) || HEM_LEN
		const d = Math.atan2(B.y - V.y, B.x - V.x) + A
		next[0] = { x: V.x + len * Math.cos(d), y: V.y + len * Math.sin(d) }
	} else {
		const V = next[n - 2], B = next[n - 3]
		const len = Math.hypot(next[n - 1].x - V.x, next[n - 1].y - V.y) || HEM_LEN
		const d = Math.atan2(B.y - V.y, B.x - V.x) - A
		next[n - 1] = { x: V.x + len * Math.cos(d), y: V.y + len * Math.sin(d) }
	}
	return { pts: next, hem: h }
}

/* qayirma uzunligi — faqat uch nuqta siljiydi */
export function setHemLenIn(pts, which, v) {
	const next = pts.map(p => ({ ...p }))
	const n = next.length
	if (which === 'first') {
		const V = next[1]
		const d = Math.atan2(next[0].y - V.y, next[0].x - V.x)
		next[0] = { x: V.x + v * Math.cos(d), y: V.y + v * Math.sin(d) }
	} else {
		const V = next[n - 2]
		const d = Math.atan2(next[n - 1].y - V.y, next[n - 1].x - V.x)
		next[n - 1] = { x: V.x + v * Math.cos(d), y: V.y + v * Math.sin(d) }
	}
	return next
}

/* ═══════════ USTAMA JADVALI ═══════════
   Mahsulotga biriktirilgan {"1":10000,"2":8000,...} jadvalidan bo'lak
   soniga mos xizmat haqini oladi (1 METR MAHSULOTGA, so'mda).
   Aniq qator bo'lmasa — eng yaqini; teng masofada kengrog'i (kichik raqam).
   Jadval bo'sh bo'lsa null qaytadi — chaqiruvchi eski `markup` ni ishlatadi.

   ⚠️ Chek qatori ham (Cashbox), chizma oynasidagi narx ko'rsatkichi ham
      SHU funksiyani chaqiradi — aks holda ikkalasi har xil narx ko'rsatardi. */
export function tierMarkup(tiers, fits) {
	// jsonb backenddan MATN bo'lib kelishi mumkin — o'zimiz ochamiz
	if (typeof tiers === 'string') { try { tiers = JSON.parse(tiers) } catch (e) { return null } }
	if (!tiers || typeof tiers !== 'object') return null
	const keys = Object.keys(tiers).map(Number).filter(n => n >= 1 && Number(tiers[n]) > 0)
	if (!keys.length || !(fits >= 1)) return null
	const key = keys.reduce((best, n) =>
		Math.abs(n - fits) < Math.abs(best - fits) ||
		(Math.abs(n - fits) === Math.abs(best - fits) && n < best) ? n : best, keys[0])
	return Number(tiers[key])
}

/* ═══════════ RASXOD ═══════════ */
export function consumption(blankMM, rollMM, remnantMin = REMNANT_MIN) {
	/* ⚠️ BO'SH CHIZMA — xato EMAS. Yangi chizma bo'sh varaqdan boshlanadi
	   (2026-08-11), o'shanda razvyortka 0 bo'ladi. Bu tekshiruvsiz `fits = 0`
	   chiqib `over = true` bo'lardi va ekranda hech qanday chiziq yo'q turib
	   «Чизма рулондан кенг (0 > 1250)» degan qizil xato yonardi. */
	if (!(blankMM > 0)) return { fits: 0, over: false, share: 0, remnant: 0, reusable: false, cons: 0 }
	// ⚠️ +1e-9 — kasr xatosi uchun. Rasxodni bo'lakka «yedirgandan» keyin
	//    razvyortka aynan rulonga bo'linadi (masalan 1250/3 = 416.6666…),
	//    epsilonsiz `1250/416.666… = 2.9999…` → floor 2 → rasxod 625 bo'lib ketardi.
	const fits = blankMM > 0 ? Math.floor(rollMM / blankMM + 1e-9) : 0
	const over = fits < 1
	const share = over ? blankMM : rollMM / fits        // teng ulush (nomdagi «Nb»)
	/* ⚠️ QOLDIQ BITTA BO'LAK. Ilgari `share − blank` («har bo'lakka 36.7»)
	   hisoblanardi va 100 mm bilan solishtirilardi. Amalda tunuka o'lchamiga
	   qarab kesiladi: 3 × 380 = 1140, rulon 1250 → BITTA 110 mm lik tasma
	   ortadi. Eski hisob uni «36.7 — juda tor» deb chiqindiga chiqarib,
	   mijozdan ortiqcha pul olardi (2026-08-08, foydalanuvchi topdi). */
	const remnant = over ? 0 : rollMM - fits * blankMM
	const reusable = !over && remnant >= remnantMin
	/* Mijoz DOIM to'liq ulushga to'laydi, ortgan tasma unga beriladi.
	   (Ilgari «omborga qoldirish» rejimi bor edi — 2026-08-09 da olib tashlandi.) */
	const cons = over ? blankMM : share
	return { fits, over, share, remnant, reusable, cons }
}

/* ═══════════ VALIDATSIYA ═══════════
   Ясаб бўлмайдиган профил чизилиб қолмаслиги учун. */
export function validateDrawing(pts, hem) {
	const S = segsOf(pts), errs = []
	const isHem = i => (i === 0 && hem.first) || (i === pts.length - 2 && hem.last)
	S.forEach((sg, i) => {
		const lim = isHem(i) ? MIN_HEM : MIN_SEG
		if (sg.len < lim) errs.push({ i, kind: 'min', msg: `${i + 1}-segment ${sg.len} mm — eng kami ${lim} mm` })
	})
	const cross = (a, b, c, d) => {
		const s1 = { x: b.x - a.x, y: b.y - a.y }, s2 = { x: d.x - c.x, y: d.y - c.y }
		const den = -s2.x * s1.y + s1.x * s2.y
		if (Math.abs(den) < 1e-9) return false
		const s = (-s1.y * (a.x - c.x) + s1.x * (a.y - c.y)) / den
		const t = (s2.x * (a.y - c.y) - s2.y * (a.x - c.x)) / den
		return s > .01 && s < .99 && t > .01 && t < .99
	}
	for (let i = 0; i < pts.length - 1; i++) {
		for (let j = i + 2; j < pts.length - 1; j++) {
			if (cross(pts[i], pts[i + 1], pts[j], pts[j + 1])) {
				errs.push({ i, kind: 'hit', msg: `${i + 1} va ${j + 1}-segment kesishyapti — bunday buklab bo'lmaydi` })
				break
			}
		}
	}
	return errs
}

/* ═══════════ 2D yo'l — qayirmada YOY ═══════════ */
export function pathWithHems(P, pts, hem, kScale) {
	const hv = []
	if (hem.first && P.length > 2) hv.push(1)
	if (hem.last && P.length > 2) hv.push(P.length - 2)
	const R = Math.max(HEM_R * (kScale || 1), 3)
	let d = `M${P[0].x.toFixed(1)},${P[0].y.toFixed(1)}`
	for (let i = 1; i < P.length; i++) {
		if (hv.includes(i) && i < P.length - 1) {
			const V = P[i], A0 = P[i - 1], B0 = P[i + 1]
			const u1 = { x: A0.x - V.x, y: A0.y - V.y }, L1 = Math.hypot(u1.x, u1.y) || 1
			const u2 = { x: B0.x - V.x, y: B0.y - V.y }, L2 = Math.hypot(u2.x, u2.y) || 1
			const t = Math.min(R, L1 * .6, L2 * .6)
			const T1 = { x: V.x + u1.x / L1 * t, y: V.y + u1.y / L1 * t }
			const T2 = { x: V.x + u2.x / L2 * t, y: V.y + u2.y / L2 * t }
			const cz = u1.x * u2.y - u1.y * u2.x
			d += ` L${T1.x.toFixed(1)},${T1.y.toFixed(1)}`
			d += ` A${t.toFixed(1)},${t.toFixed(1)} 0 0 ${cz > 0 ? 1 : 0} ${T2.x.toFixed(1)},${T2.y.toFixed(1)}`
		} else {
			d += ` L${P[i].x.toFixed(1)},${P[i].y.toFixed(1)}`
		}
	}
	return d
}

/* ═══════════ 3D kesim konturi ═══════════
   Markaziy chiziqni ikki tomonga surib yopiq kontur.
   O'tkir burilishda (qayirma) TASHQI tomon YOY bo'lib aylanadi. */
function offsetSide(pts, T, sign) {
	const N = pts.length, out = []
	const dirOf = i => {
		const a = pts[i], b = pts[i + 1], L = Math.hypot(b.x - a.x, b.y - a.y) || 1
		return { x: (b.x - a.x) / L, y: (b.y - a.y) / L }
	}
	const nOf = d => ({ x: -d.y * sign, y: d.x * sign })
	const d0 = dirOf(0), n0 = nOf(d0)
	out.push({ x: pts[0].x + n0.x * T, y: pts[0].y + n0.y * T })
	for (let i = 1; i < N - 1; i++) {
		const d1 = dirOf(i - 1), d2 = dirOf(i)
		const n1 = nOf(d1), n2 = nOf(d2)
		const turn = Math.atan2(d1.x * d2.y - d1.y * d2.x, d1.x * d2.x + d1.y * d2.y)
		if ((turn * sign) < 0 && Math.abs(turn) > 1.4) {
			const a1 = Math.atan2(n1.y, n1.x)
			let da = Math.atan2(n2.y, n2.x) - a1
			while (da > Math.PI) da -= 2 * Math.PI
			while (da < -Math.PI) da += 2 * Math.PI
			for (let k = 0; k <= 12; k++) {
				const a = a1 + da * k / 12
				out.push({ x: pts[i].x + Math.cos(a) * T, y: pts[i].y + Math.sin(a) * T })
			}
		} else {
			let mx = n1.x + n2.x, my = n1.y + n2.y
			const ml = Math.hypot(mx, my) || 1
			mx /= ml; my /= ml
			const cosHalf = Math.max(.25, n1.x * mx + n1.y * my)
			const s = Math.min(T / cosHalf, T * 4)
			out.push({ x: pts[i].x + mx * s, y: pts[i].y + my * s })
		}
	}
	const dN = dirOf(N - 2), nN = nOf(dN)
	out.push({ x: pts[N - 1].x + nN.x * T, y: pts[N - 1].y + nN.y * T })
	return out
}

export function crossSection(pts, halfThickness) {
	if (pts.length < 2) return []
	const T = halfThickness || 2
	const up = offsetSide(pts, T, +1).map(p => ({ ...p, c: 1 }))
	const dn = offsetSide(pts, T, -1).map(p => ({ ...p, c: 0 }))
	return up.concat(dn.reverse())
}

/* ═══════════ YORLIQ JOYLASHTIRISH ═══════════
   O'lcham va gradus yorliqlari bir-birining ustiga chiqib ketmasin.
   Har biri chiziqdan uzoqlashtiriladi, joy topilmaguncha bosqichma-bosqich.
   Bu ANIQ yechim emas, lekin amalda hamma raqam ko'rinadigan bo'ladi. */
/* @param wanted  joylashtiriladigan yorliqlar
 * @param pts     CHIZMANING nuqtalari (ixtiyoriy). Berilsa, yorliq chizma
 *                chiziqlari ustiga TUSHMAYDI.
 *
 * ⚠️ Ilgari bu funksiya faqat yorliqlarning bir-biriga tegishini tekshirardi.
 *    Natijada razmer raqami chizmaning o'ziga YOPISHIB qolardi — ayniqsa
 *    yorliq qutisi kattalashtirilgandan keyin (2026-08-14, foydalanuvchi
 *    shikoyati). Endi `pts` berilsa har bir nomzod holat chiziqlar bilan
 *    ham tekshiriladi.
 */
export function placeLabels(wanted, pts) {
	const done = []
	const hit = (a, b) =>
		Math.abs(a.x - b.x) < (a.w + b.w) / 2 + 3 &&
		Math.abs(a.y - b.y) < (a.h + b.h) / 2 + 3

	/* Quti chizma chizig'ini kesib o'tyaptimi.
	   Kesishishni aniq hisoblash o'rniga chiziq bo'ylab NUQTA TASHLAYMIZ va
	   ular qutiga tushyaptimi — shuni qaraymiz. Bizning masshtabda (820 birlik)
	   6 birliklik qadam yetarli va bu usul burchak/yoy holatlarida ham
	   ishonchli, chunki geometriyaga bog'liq emas. */
	const segs = []
	if (Array.isArray(pts) && pts.length > 1) {
		for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]])
	}
	const overShape = (c) => {
		if (!segs.length) return false
		const hw = c.w / 2 + 4, hh = c.h / 2 + 4
		for (const [a, b] of segs) {
			const dx = b.x - a.x, dy = b.y - a.y
			const steps = Math.max(2, Math.ceil(Math.hypot(dx, dy) / 6))
			for (let s = 0; s <= steps; s++) {
				const t = s / steps
				const px = a.x + dx * t, py = a.y + dy * t
				if (Math.abs(px - c.x) < hw && Math.abs(py - c.y) < hh) return true
			}
		}
		return false
	}

	for (const L of wanted) {
		let best = null
		// 1) o'z normalidan uzoqlashtiramiz, 2) keyin teskari tomonga
		for (const sign of [1, -1]) {
			for (const mul of [1, 1.55, 2.15, 2.8, 3.5, 4.3]) {
				const c = {
					...L,
					x: L.ax + L.nx * L.off * mul * sign,
					y: L.ay + L.ny * L.off * mul * sign,
				}
				if (!done.some(d => hit(c, d)) && !overShape(c)) { best = c; break }
			}
			if (best) break
		}
		/* Joy topilmasa — hech bo'lmasa chizmadan CHETROQQA suramiz (2.8×),
		   avvalgidek ustiga tashlab ketmaymiz. */
		if (!best) {
			const far = { ...L, x: L.ax + L.nx * L.off * 2.8, y: L.ay + L.ny * L.off * 2.8 }
			best = overShape(far) ? { ...L, x: L.ax + L.nx * L.off * 4.3, y: L.ay + L.ny * L.off * 4.3 } : far
		}
		done.push(best)
	}
	return done
}

/* ═══════════════════════════════════════════════════════════════════════════
   CHOP ETISH UCHUN CHIZMA — STATIK SVG (2026-08-13)

   NEGA KERAK: ilgari chekka `svgToDataUri()` faqat YALANG'OCH KONTURni
   chiqarardi — o'lchamlar, burchaklar va rang tomoni ekrandagi chizmada bor
   edi, eksportга esa umuman tushmasdi. Usta chekда shaklni ko'rardi-yu,
   nechа millimetrdan bukishni bilmasdi.

   Bu funksiya EKRAN bilan bir xil geometriya yordamchilaridan foydalanadi
   (`segsOf`, `pathWithHems`, `placeLabels`, `toBend`) — shuning uchun ikkisi
   kelajakda ham ajralib ketmaydi.

   Tahrirlash bezaklari (tutqichlar, tanlangan segment, hit-box) ATAYLAB
   chizilmaydi — chop etishда ular faqat xalaqit beradi.

   ⚠️ TERMAL PRINTER (80 mm) uchun moslangan:
      · chiziq qalin (mayda chiziq bosilmay qoladi)
      · yorliq foni oq to'ldirilgan (kul rang fon dog' bo'lib chiqadi)
      · matn qora va yirik (kulrang matn o'qilmaydi)
      · rang strelkasi to'q konturli (och rang termal qog'ozda yo'qoladi)
═══════════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════════
   YORLIQ O'LCHAMLARI — BITTA JOYDA (2026-08-14)

   80 mm chek: SVG 820 birlik keng, qog'ozda ~72 mm → 1 birlik ≈ 0.088 mm.

   ⚠️ `gap` NIMA UCHUN KERAK: yorliq chiziqdan `h/2 + gap` masofaga
      qo'yiladi. Ilgari siljish `26` deb QATTIQ yozilgan edi va quti
      balandligi ham 52 bo'lgach (yarmi = 26) quti chiziqqa AYNAN tegib
      turardi — raqam chizmaga yopishib qolardi.
      Endi siljish o'lchamdan KELIB CHIQADI: qutini kattalashtirsangiz
      u o'zi uzoqlashadi, qo'lda tuzatish shart emas.

   ⚠️ `placeLabels()` faqat yorliqlarning BIR-BIRIGA tegishini tekshiradi,
      chizmaning O'ZIGA tegishini emas. Shuning uchun boshlang'ich masofa
      to'g'ri bo'lishi SHART — u yagona himoya. */
const LBL = {
	len: { w: 104, h: 52 },   // razmer — eng muhim raqam
	ang: { w: 60, h: 30 },    // gradus — yordamchi, kichik
	gap: 16,                  // chiziq bilan quti orasidagi bo'shliq
}

export function drawingSvg(opts) {
	const {
		pts, hem = {}, colourTop = true,
		coilHex = '#888',
		width = 820, height = 520,
		pad = 62,              // yorliqlar chetga chiqib ketmasligi uchun
		forPrint = true,
		/* KIMGA CHIZILAYAPTI (2026-08-13, foydalanuvchi qarori):
		     usta   → showDims + showAngles + showColour  (hammasi)
		     mijoz  → showDims                            (burchaksiz)
		   Burchak va rang tomoni — SEXNING ko'rsatmasi; mijoz chekida ular
		   faqat shovqin. O'lchamlar esa mijozga kerak: nizo chiqsa
		   «men buni buyurtma qilmagandim» degan gapga dalil bo'ladi. */
		showDims = true,
		showAngles = true,
		showColour = true,
	} = opts || {}

	if (!Array.isArray(pts) || pts.length < 2) return null

	const S = segsOf(pts)
	if (!S.length) return null

	// ── masshtab (DrawingModal dagi fit() bilan bir xil mantiq) ──
	const xs = pts.map(p => p.x), ys = pts.map(p => p.y)
	const minX = Math.min(...xs), maxX = Math.max(...xs)
	const minY = Math.min(...ys), maxY = Math.max(...ys)
	const sw = Math.max(maxX - minX, 40), sh = Math.max(maxY - minY, 40)
	const k = Math.min((width - pad * 2) / sw, (height - pad * 2) / sh)
	const ox = pad + (width - pad * 2 - sw * k) / 2
	const oy = pad + (height - pad * 2 - sh * k) / 2
	const P = pts.map(p => ({ x: ox + (p.x - minX) * k, y: oy + (p.y - minY) * k }))

	const ink = '#000'
	const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
	const n = v => (Math.round(v * 10) / 10)
	const parts = []

	parts.push(`<rect width="100%" height="100%" fill="#fff"/>`)

	// ── RANG TOMONI: eng uzun 3 segmentga strelka ──
	const cSign = colourTop ? 1 : -1
	if (showColour) S.map((s, i) => ({ i, len: s.len })).sort((a, b) => b.len - a.len).slice(0, 3)
		.forEach(({ i }) => {
			const a = P[i], b = P[i + 1]
			const dx = b.x - a.x, dy = b.y - a.y, LL = Math.hypot(dx, dy) || 1
			if (LL < 30) return
			const nx = -dy / LL * cSign, ny = dx / LL * cSign
			const mx = a.x + dx * 0.32, my = a.y + dy * 0.32
			const at = d => ({ x: mx + nx * d, y: my + ny * d })
			/* O'LCHAM — 80 mm chekka qarab (1 birlik ≈ 0.088 mm).
			   Eski: uzunlik 33 birlik ≈ 2.9 mm, boshi 5.6 ≈ 0.5 mm — qog'ozda
			   ko'rinmas nuqta edi. Endi: ~4.6 mm uzunlik, boshi ~0.9 mm. */
			const tip = at(4), base = at(26), tail = at(56)
			const px = ny * 10, py = -nx * 10
			const head = `M${n(tip.x)},${n(tip.y)} L${n(base.x + px)},${n(base.y + py)} L${n(base.x - px)},${n(base.y - py)} Z`
			/* ⚠️ STRELKA QORA CHIZILADI, RULON RANGIDA EMAS (2026-08-14).
			   Ilgari `fill="${coilHex}"` edi — sukut bo'yicha `#888` kulrang.
			   Termal printer kulrangni siyraklashtirib chizadi, natijada
			   strelka XIRA yoki umuman ko'rinmas bo'lib qolardi. Qora-oq
			   chopda rulon rangi hech qanday ma'lumot bermaydi — rang tomonini
			   strelkaning YO'NALISHI ko'rsatadi, rangi emas.
			   Oq halo (7 va 3.6 kenglik) chizma chizig'i ustida ajratib turadi. */
			parts.push(`<line x1="${n(tail.x)}" y1="${n(tail.y)}" x2="${n(base.x)}" y2="${n(base.y)}" stroke="#fff" stroke-width="8" stroke-linecap="round"/>`)
			parts.push(`<path d="${head}" fill="#fff" stroke="#fff" stroke-width="4.2" stroke-linejoin="round"/>`)
			parts.push(`<line x1="${n(tail.x)}" y1="${n(tail.y)}" x2="${n(base.x)}" y2="${n(base.y)}" stroke="${ink}" stroke-width="4" stroke-linecap="round"/>`)
			parts.push(`<path d="${head}" fill="${ink}" stroke="${ink}" stroke-width="1.2" stroke-linejoin="round"/>`)
		})

	// ── ASOSIY KONTUR (qayirmalar bilan) ──
	parts.push(`<path d="${pathWithHems(P, pts, hem, k)}" fill="none" stroke="${ink}" stroke-width="${forPrint ? 4 : 2.8}" stroke-linejoin="round" stroke-linecap="round"/>`)

	// ── BURCHAK YOYLARI + YORLIQLAR ──
	const wanted = []
	if (showAngles) S.forEach((sg, i) => {
		if (sg.ang == null) return
		const p = P[i - 1], v = P[i], nn = P[i + 1], R = 30
		if (!p || !v || !nn) return
		const a1 = Math.atan2(p.y - v.y, p.x - v.x)
		let d = Math.atan2(nn.y - v.y, nn.x - v.x) - a1
		while (d <= -Math.PI) d += 2 * Math.PI
		while (d > Math.PI) d -= 2 * Math.PI
		const x1 = v.x + R * Math.cos(a1), y1 = v.y + R * Math.sin(a1)
		const x2 = v.x + R * Math.cos(a1 + d), y2 = v.y + R * Math.sin(a1 + d)
		const mid = a1 + d / 2
		parts.push(`<path d="M${n(x1)},${n(y1)} A${R},${R} 0 0 ${d > 0 ? 1 : 0} ${n(x2)},${n(y2)}" fill="none" stroke="${ink}" stroke-width="1.6"/>`)
		wanted.push({
			/* ⚠️ YORLIQ O'LCHAMI — 80 mm CHEKKA qarab (2026-08-14).
			   SVG 820 birlik keng, chekda ~72 mm ga siqiladi: 1 birlik ≈ 0.088 mm.

			   BURCHAK (gradus) — KICHIK qoladi (foydalanuvchi qarori):
			   usta burchakni stanokdan biladi, u yordamchi ma'lumot.
			   Kichik bo'lgani ayni muddao — RAZMERGA joy bo'shatadi. */
			kind: 'ang', i, w: LBL.ang.w, h: LBL.ang.h,
			ax: v.x, ay: v.y, nx: Math.cos(mid), ny: Math.sin(mid),
			off: R + LBL.ang.h / 2 + LBL.gap,
			// USTAGA BUKILISH ko'rsatiladi (ichki 135° → 45°) — ekran bilan bir xil
			text: toBend(sg.ang) + '\u00B0',
		})
	})

	// ── SEGMENT O'LCHAMLARI (mm) ──
	if (showDims) S.forEach((sg, i) => {
		const a = P[i], b = P[i + 1]
		const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2
		const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy) || 1
		wanted.push({
			/* RAZMER (uzunlik) — ENG MUHIM RAQAM, eng katta bo'lsin.
			   Usta shu raqamga qarab kesadi; xato o'qilsa material nobud bo'ladi.
			   Qog'ozda ~3.5 mm — qo'lda ushlab turib ham o'qiladi. */
			kind: 'len', i, w: LBL.len.w, h: LBL.len.h,
			ax: mx, ay: my, nx: -dy / L, ny: dx / L,
			off: LBL.len.h / 2 + LBL.gap,
			text: String(sg.len),
		})
	})

	const placed = placeLabels(wanted, P)
	for (const L of placed) {
		const isAng = L.kind === 'ang'
		parts.push(`<line x1="${n(L.ax)}" y1="${n(L.ay)}" x2="${n(L.x)}" y2="${n(L.y)}" stroke="${ink}" stroke-width="1" stroke-dasharray="3 3"/>`)
		/* SHRIFT: razmer KATTA (38), burchak kichik (22) — foydalanuvchi qarori.
		   Vertikal markazlash shriftga BOG'LIQ: qattiq `y + 10` yozilsa, shrift
		   o'zgarganda matn qutidan chiqib ketardi. `fs * 0.35` — Arial uchun
		   x-balandlikning yarmiga yaqin, ya'ni raqam qutida markazda turadi. */
		const fs = isAng ? 22 : 38
		parts.push(`<rect x="${n(L.x - L.w / 2)}" y="${n(L.y - L.h / 2)}" width="${L.w}" height="${L.h}" rx="4" fill="#fff" stroke="${ink}" stroke-width="${isAng ? 1.6 : 2.4}"/>`)
		parts.push(`<text x="${n(L.x)}" y="${n(L.y + fs * 0.35)}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="${fs}" font-weight="${isAng ? 700 : 800}" fill="${ink}">${esc(L.text)}</text>`)
	}

	/* ═══════════ CHEGARANI CHIZILGANGA MOSLASH (2026-08-14) ═══════════
	   Ilgari chegara QATTIQ `0 0 820 520` edi. Chizma shu doiraga sig'dirilardi,
	   lekin YORLIQLAR undan tashqariga chiqishi mumkin: joy topilmagan yorliq
	   `off × 4.3` masofagacha suriladi, ya'ni ~180 birlik — `pad` esa atigi 62.
	   Natijada murakkab chizmada (5 segment = 9 yorliq) razmerlar chegaradan
	   chiqib QIRQILARDI. Foydalanuvchi shikoyati: «chizma kattaroq bo'lsa
	   razmerlar ko'rinmay qolyapti» (chek 8569, 5-chizma).

	   Endi hamma narsa chizilgach O'LCHAB olinadi va chegara shunga qarab
	   kengaytiriladi. Rasm `width: 100%` bilan joylashtirilgani uchun natija:
	   hech narsa qirqilmaydi, faqat butun chizma bir oz kichrayadi. */
	let x0 = 0, y0 = 0, x1 = width, y1 = height
	const grow = (bx0, by0, bx1, by1) => {
		if (bx0 < x0) x0 = bx0
		if (by0 < y0) y0 = by0
		if (bx1 > x1) x1 = bx1
		if (by1 > y1) y1 = by1
	}
	for (const L of placed) {
		grow(L.x - L.w / 2, L.y - L.h / 2, L.x + L.w / 2, L.y + L.h / 2)
	}
	for (const p of P) grow(p.x, p.y, p.x, p.y)

	const M = 8   // yupqa hoshiya — chegara chizig'i qirqilmasin
	x0 -= M; y0 -= M; x1 += M; y1 += M
	const vw = Math.round(x1 - x0), vh = Math.round(y1 - y0)

	return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${n(x0)} ${n(y0)} ${vw} ${vh}" width="${vw}" height="${vh}">`
		+ parts.join('') + `</svg>`
}

/** `drawingSvg()` natijasini <img src> uchun data-uri ga o'giradi. */
export function drawingDataUri(opts) {
	const svg = drawingSvg(opts)
	if (!svg) return null
	try {
		return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)))
	} catch (e) { return null }
}
